import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { aiApi, GenerateOptions } from '@/api/ai';

export type AiGenerationPhase = 'idle' | 'running' | 'cancelling';

export interface AiGenerationHandle {
    /** Hand these to `aiApi.generate` so the run can be stopped. */
    options: GenerateOptions;
}

export interface AiGenerationRunner {
    phase: AiGenerationPhase;
    begin: () => AiGenerationHandle;
    end: () => void;
    cancel: () => Promise<void>;
}

/**
 * Owns the single in-flight AI generation: its abort controller, the id of the
 * background job it turned into, and which of the three phases it is in.
 *
 * It lives here rather than in each section's handler because stopping needs
 * two things at once that no single section has: the controller, held by
 * whoever called `generate`, and the job id, which only arrives mid-flight in a
 * 202 the caller never sees. The editor allows one generation at a time, so one
 * runner is enough for the whole report.
 *
 * Cancelling is deliberately two-sided. Aborting locally is instant and stops
 * this tab from applying a draft it no longer wants; cancelling the job is what
 * stops the model from being paid for. Neither alone is a stop.
 */
export function useAiGenerationRunner(): AiGenerationRunner {
    const [phase, setPhase] = useState<AiGenerationPhase>('idle');
    const runRef = useRef<{
        controller: AbortController;
        jobId: string | null;
        cancelled: boolean;
    } | null>(null);

    const begin = useCallback((): AiGenerationHandle => {
        runRef.current?.controller.abort();

        const controller = new AbortController();
        const run = { controller, jobId: null as string | null, cancelled: false };
        runRef.current = run;
        setPhase('running');

        return {
            options: {
                signal: controller.signal,
                onJobStarted: (jobId) => {
                    run.jobId = jobId;
                },
            },
        };
    }, []);

    const end = useCallback(() => {
        // Announced here rather than at the click, because at the click nothing
        // has stopped yet — the button still says "Interrompendo…". This is the
        // moment the section is actually back to what it was.
        if (runRef.current?.cancelled) {
            toast.success('Geração interrompida', {
                description: 'Nenhum crédito de IA foi usado.',
            });
        }

        runRef.current = null;
        setPhase('idle');
    }, []);

    const cancel = useCallback(async () => {
        const run = runRef.current;
        if (!run || run.cancelled) return;

        run.cancelled = true;
        setPhase('cancelling');

        // The job outlives this tab's request, so it is asked to stop before
        // anything is aborted here — the local abort would drop the id needed
        // to reach it. Awaiting the answer is what `cancelling` is for: until
        // it lands, the worker has not been told, and the toast that follows
        // promises no credit was spent.
        if (run.jobId) {
            await aiApi.cancelJob(run.jobId).catch(() => {
                // Stopped for the user either way. The worker finishes a draft
                // that will never be served — `discardDraftForJob` retires it
                // on the next cancel, and an unserved draft costs no credit.
            });
        }

        // Guards against a run that ended on its own while the cancel was in
        // flight: `end` cleared the ref, and there is nothing left to abort.
        if (runRef.current === run) run.controller.abort();
    }, []);

    // A generation that outlives the editor has no one left to apply it.
    useEffect(() => () => runRef.current?.controller.abort(), []);

    return { phase, begin, end, cancel };
}
